/**
 * Hook useTheme - Accesso semplificato alla configurazione del tema
 * 
 * IMPORTANTE: Usa livePreview per decidere quale tema mostrare:
 * - livePreview = true: mostra previewTheme (modifiche temporanee)
 * - livePreview = false: mostra savedTheme (modifiche salvate)
 */

import { useMemo } from 'react';
import { useThemeStore } from '../stores/themeStore';
import { ThemeConfig, ThemeMode } from '../types/theme';

/**
 * Hook principale per accedere al tema
 * Restituisce savedTheme se livePreview è false, previewTheme se true
 */
export function useTheme(): ThemeConfig {
  // Subscribe to all three to trigger re-render when any changes
  const livePreview = useThemeStore((state) => state.livePreview);
  const previewTheme = useThemeStore((state) => state.previewTheme);
  const savedTheme = useThemeStore((state) => state.savedTheme);
  
  return useMemo(() => {
    const theme = livePreview ? previewTheme : savedTheme;
    // Return deep copy to ensure reference changes trigger re-renders
    return JSON.parse(JSON.stringify(theme)) as ThemeConfig;
  }, [livePreview, previewTheme, savedTheme]);
}

/**
 * Hook per i colori del tema
 */
export function useThemeColors() {
  const theme = useTheme();
  return theme.colors;
}

/**
 * Hook per la tipografia - returns simplified object
 */
export function useThemeTypography() {
  // Return empty object since typography was removed
  return {};
}

/**
 * Hook per la modalità (light/dark)
 */
export function useThemeMode(): ThemeMode {
  const mode = useThemeStore((state) => state.mode);
  return mode;
}

/**
 * Hook per il tema glassmorphism
 */
export function useThemeGlass() {
  const theme = useTheme();
  return theme.glass;
}

/**
 * Hook per i bottoni - returns simplified object
 */
export function useThemeButtons() {
  return {};
}

/**
 * Hook per le cards - returns simplified object  
 */
export function useThemeCards() {
  return {};
}

/**
 * Hook per la navigazione
 */
export function useThemeNavigation() {
  const theme = useTheme();
  return theme.navigation;
}

/**
 * Hook per le azioni sul tema (setColor, saveTheme, etc.)
 */
export function useThemeActions() {
  const setColor = useThemeStore((state) => state.setColor);
  const setTheme = useThemeStore((state) => state.setTheme);
  const saveTheme = useThemeStore((state) => state.saveTheme);
  const resetTheme = useThemeStore((state) => state.resetTheme);
  const discardPreview = useThemeStore((state) => state.discardPreview);
  const toggleLivePreview = useThemeStore((state) => state.toggleLivePreview);
  const undo = useThemeStore((state) => state.undo);
  const redo = useThemeStore((state) => state.redo);
  const exportTheme = useThemeStore((state) => state.exportTheme);
  const importTheme = useThemeStore((state) => state.importTheme);
  
  return {
    setColor,
    setTheme,
    saveTheme,
    resetTheme,
    discardPreview,
    toggleLivePreview,
    undo,
    redo,
    exportTheme,
    importTheme,
  };
}

/**
 * Hook per i preset del tema
 */
export function useThemePresets() {
  const presets = useThemeStore((state) => state.presets);
  const currentPresetId = useThemeStore((state) => state.currentPresetId);
  const savePreset = useThemeStore((state) => state.savePreset);
  const loadPreset = useThemeStore((state) => state.loadPreset);
  const deletePreset = useThemeStore((state) => state.deletePreset);
  
  return {
    presets,
    currentPresetId,
    savePreset,
    loadPreset,
    deletePreset,
  };
}
